import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import { LeftArrow, RightArrow } from '../assets/SvgComponents';

type Breakpoints = { base?: number; sm?: number; lg?: number };

type CarouselProps = {
  children: React.ReactNode | React.ReactNode[];
  options?: EmblaOptionsType;
  gap?: number;
  ariaLabel?: string;
  slidesToShow?: Breakpoints;
  overlayArrows?: boolean;
  loop?: boolean;
  navButtonLeftId?: string;
  navButtonRightId?: string;
};

const Carousel: React.FC<CarouselProps> = ({
  children,
  options,
  gap = 16,
  ariaLabel,
  slidesToShow,
  overlayArrows = false,
  loop = true,
  navButtonLeftId,
  navButtonRightId
}) => {
  const memoOptions = useMemo<EmblaOptionsType>(
    () => ({ align: 'start', loop, ...options }),
    [options, loop]
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(memoOptions);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, action: () => void) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const slides = useMemo(
    () => (Array.isArray(children) ? children : [children]),
    [children]
  );

  return (
    <CarouselRoot
      role='region'
      aria-roledescription='carousel'
      aria-label={ariaLabel || 'Carrusel'}
    >
      <NavButton
        type='button'
        id={navButtonLeftId}
        side='left'
        overlay={overlayArrows}
        aria-label='Anterior'
        onClick={handlePrev}
        onKeyDown={e => handleKeyDown(e, handlePrev)}
        tabIndex={0}
        disabled={!canPrev}
      >
        <LeftArrow />
      </NavButton>

      <Viewport ref={emblaRef}>
        <Container gap={gap}>
          {slides.map((child, index) => (
            <Slide
              key={index}
              gap={gap}
              slidesToShow={slidesToShow}
              aria-roledescription='slide'
              aria-label={`Diapositiva ${index + 1}`}
            >
              {child}
            </Slide>
          ))}
        </Container>
      </Viewport>

      <NavButton
        type='button'
        id={navButtonRightId}
        side='right'
        overlay={overlayArrows}
        aria-label='Siguiente'
        onClick={handleNext}
        onKeyDown={e => handleKeyDown(e, handleNext)}
        tabIndex={0}
        disabled={!canNext}
      >
        <RightArrow />
      </NavButton>
    </CarouselRoot>
  );
};

export default Carousel;

const CarouselRoot = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  @media (min-width: 600px) {
    padding: 0 16px;
  }
  @media (min-width: 1024px) {
    /* padding: 0 64px; */
  }
`;

const Viewport = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 1rem;
`;

const Container = styled.div<{ gap: number }>`
  display: flex;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  margin-left: ${({ gap }) => `-${gap}px`};
`;

const Slide = styled.div<{ gap: number; slidesToShow?: Breakpoints }>`
  position: relative;
  padding-left: ${({ gap }) => `${gap}px`};
  margin-right: ${({ gap }) => `${gap}px`};
  flex: 0 0
    ${({ slidesToShow }) =>
      slidesToShow?.base ? `${100 / slidesToShow.base}%` : '85%'};
  @media (min-width: 600px) {
    width: auto;
    margin-left: 0px;
    flex: 0 0
      ${({ slidesToShow }) =>
        slidesToShow?.sm ? `${100 / slidesToShow.sm}%` : '50%'};
  }
  @media (min-width: 1024px) {
    flex: 0 0
      ${({ slidesToShow }) =>
        slidesToShow?.lg ? `${100 / slidesToShow.lg}%` : '33.3333%'};
  }
`;

const NavButton = styled.button<{ side: 'left' | 'right'; overlay: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px !important;
  width: 25px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  transition: opacity 0.2s ease;
  opacity: 1;
  flex-shrink: 0;
  position: ${({ overlay }) => (overlay ? 'absolute' : 'static')};
  top: ${({ overlay }) => (overlay ? '50%' : 'auto')};
  left: ${({ overlay, side }) => (overlay && side === 'left' ? '0' : 'auto')};
  right: ${({ overlay, side }) => (overlay && side === 'right' ? '0' : 'auto')};
  transform: ${({ overlay }) => (overlay ? 'translateY(-50%)' : 'none')};
  z-index: 1;
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
  @media (min-width: 600px) {
    min-width: 45px !important;
    width: 40px;
    height: 40px;
  }
`;
